import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";

export interface OnboardingData {
    personalDetails?: {
        name: string;
        city: string;
    };
    panNumber?: string;
    bankDetails?: {
        accountNumber: string;
        reEnterAccountNumber: string;
        bank: string;
        ifscCode: string;
    };
}

export interface OnboardingContextType {
    onboardingData: OnboardingData;
    setOnboardingData: React.Dispatch<React.SetStateAction<OnboardingData>>;
    
    // Personal details form state
    currentFormData: {
        name: string;
        city: string;
        panNumber: string;
    };
    setCurrentFormData: React.Dispatch<React.SetStateAction<{
        name: string;
        city: string;
        panNumber: string;
    }>>;
    
    // Bank details form state
    bankFormData: {
        accountNumber: string;
        reEnterAccountNumber: string;
        bank: string;
        ifscCode: string;
    };
    setBankFormData: React.Dispatch<React.SetStateAction<{
        accountNumber: string;
        reEnterAccountNumber: string;
        bank: string;
        ifscCode: string;
    }>>;
    
    // Reset function to clear all onboarding data
    resetOnboardingData: () => void;
    
    // PAN modal state
    showPANModal: boolean;
    setShowPANModal: React.Dispatch<React.SetStateAction<boolean>>;
    panValidationResult: { isValid: boolean; name: string; maskedPAN: string } | null;
    setPanValidationResult: React.Dispatch<React.SetStateAction<{ isValid: boolean; name: string; maskedPAN: string } | null>>;
    
    // Bank modal state
    showBankModal: boolean;
    setShowBankModal: React.Dispatch<React.SetStateAction<boolean>>;
    bankValidationResult: { isValid: boolean; bankName: string; maskedAccountNumber: string; errorMessage?: string } | null;
    setBankValidationResult: React.Dispatch<React.SetStateAction<{ isValid: boolean; bankName: string; maskedAccountNumber: string; errorMessage?: string } | null>>;
    
    // Error states
    panError: {
        status_code: number;
        status_message: string;
        data: {
            error_message: string;
            error_code: string;
        };
    } | null;
    setPanError: React.Dispatch<React.SetStateAction<{
        status_code: number;
        status_message: string;
        data: {
            error_message: string;
            error_code: string;
        };
    } | null>>;
    
    personalDetailsError: {
        status_code: number;
        status_message: string;
        data: {
            error_message: string;
            error_code: string;
        };
    } | null;
    setPersonalDetailsError: React.Dispatch<React.SetStateAction<{
        status_code: number;
        status_message: string;
        data: {
            error_message: string;
            error_code: string;
        };
    } | null>>;
    
    // Loading states
    isSavingPersonalDetails: boolean;
    setIsSavingPersonalDetails: React.Dispatch<React.SetStateAction<boolean>>;
    isSavingPanDetails: boolean;
    setIsSavingPanDetails: React.Dispatch<React.SetStateAction<boolean>>;
    isConfirmingPanDetails: boolean;
    setIsConfirmingPanDetails: React.Dispatch<React.SetStateAction<boolean>>;
    isSavingBankDetails: boolean;
    setIsSavingBankDetails: React.Dispatch<React.SetStateAction<boolean>>;
}

const OnboardingContext = createContext<OnboardingContextType | null>(null);

const STORAGE_KEY = 'onboarding_form_data';

// Helper functions for localStorage
const loadFromStorage = (): Partial<OnboardingContextType> | null => {
    if (typeof window === 'undefined') return null;
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (error) {
        console.error('Error loading onboarding data from storage:', error);
    }
    return null;
};

const saveToStorage = (data: Partial<OnboardingContextType>) => {
    if (typeof window === 'undefined') return;
    try {
        // Only save form data, not loading states or modals
        const dataToSave = {
            onboardingData: data.onboardingData,
            currentFormData: data.currentFormData,
            bankFormData: data.bankFormData,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (error) {
        console.error('Error saving onboarding data to storage:', error);
    }
};

const clearStorage = () => {
    if (typeof window === 'undefined') return;
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
        console.error('Error clearing onboarding data from storage:', error);
    }
};

export const OnboardingProvider = ({ children }: { children: ReactNode }) => {
    // Initialize state with defaults to ensure SSR/client consistency
    // We'll hydrate from localStorage after mount
    const [onboardingData, setOnboardingData] = useState<OnboardingData>({});
    const [currentFormData, setCurrentFormData] = useState({
        name: '',
        city: '',
        panNumber: ''
    });
    const [bankFormData, setBankFormData] = useState({
        accountNumber: '',
        reEnterAccountNumber: '',
        bank: '',
        ifscCode: ''
    });
    const [isHydrated, setIsHydrated] = useState(false);

    // Reset all onboarding data to defaults
    const resetOnboardingData = React.useCallback(() => {
        setOnboardingData({});
        setCurrentFormData({
            name: '',
            city: '',
            panNumber: ''
        });
        setBankFormData({
            accountNumber: '',
            reEnterAccountNumber: '',
            bank: '',
            ifscCode: ''
        });
    }, []);

    // Hydrate from localStorage after mount to prevent hydration mismatch
    useEffect(() => {
        const stored = loadFromStorage();
        if (stored) {
            if (stored.onboardingData) {
                setOnboardingData(stored.onboardingData);
            }
            if (stored.currentFormData) {
                setCurrentFormData(stored.currentFormData);
            }
            if (stored.bankFormData) {
                setBankFormData(stored.bankFormData);
            }
        }
        setIsHydrated(true);
    }, []);
    const [showPANModal, setShowPANModal] = useState(false);
    const [panValidationResult, setPanValidationResult] = useState<{ isValid: boolean; name: string; maskedPAN: string } | null>(null);
    const [showBankModal, setShowBankModal] = useState(false);
    const [bankValidationResult, setBankValidationResult] = useState<{ isValid: boolean; bankName: string; maskedAccountNumber: string; errorMessage?: string } | null>(null);
    const [panError, setPanError] = useState<{
        status_code: number;
        status_message: string;
        data: {
            error_message: string;
            error_code: string;
        };
    } | null>(null);
    const [personalDetailsError, setPersonalDetailsError] = useState<{
        status_code: number;
        status_message: string;
        data: {
            error_message: string;
            error_code: string;
        };
    } | null>(null);
    const [isSavingPersonalDetails, setIsSavingPersonalDetails] = useState(false);
    const [isSavingPanDetails, setIsSavingPanDetails] = useState(false);
    const [isConfirmingPanDetails, setIsConfirmingPanDetails] = useState(false);
    const [isSavingBankDetails, setIsSavingBankDetails] = useState(false);

    // Persist form data to localStorage whenever it changes (only after hydration)
    useEffect(() => {
        if (isHydrated) {
            saveToStorage({
                onboardingData,
                currentFormData,
                bankFormData,
            });
        }
    }, [onboardingData, currentFormData, bankFormData, isHydrated]);

    return (
        <OnboardingContext.Provider
            value={{
                onboardingData,
                setOnboardingData,
                currentFormData,
                setCurrentFormData,
                bankFormData,
                setBankFormData,
                resetOnboardingData,
                showPANModal,
                setShowPANModal,
                panValidationResult,
                setPanValidationResult,
                showBankModal,
                setShowBankModal,
                bankValidationResult,
                setBankValidationResult,
                panError,
                setPanError,
                personalDetailsError,
                setPersonalDetailsError,
                isSavingPersonalDetails,
                setIsSavingPersonalDetails,
                isSavingPanDetails,
                setIsSavingPanDetails,
                isConfirmingPanDetails,
                setIsConfirmingPanDetails,
                isSavingBankDetails,
                setIsSavingBankDetails,
            }}
        >
            {children}
        </OnboardingContext.Provider>
    );
};

// Export function to clear onboarding storage and reset context state
export const clearOnboardingStorage = () => {
    clearStorage();
    // Note: To reset context state, use resetOnboardingData from useOnboarding hook
    // This function only clears localStorage
};

export const useOnboarding = (): OnboardingContextType => {
    const context = useContext(OnboardingContext);
    if (!context) {
        throw new Error("useOnboarding must be used within OnboardingProvider");
    }
    return context;
};

